import _ from 'lodash';

import CreateComposition from './lib/create_composition';
import { MessageChannelType } from 'models/conversation_message';
import { getLatestManualItem } from 'scripts/application/lib/conversation_history_helpers';
import ConversationMessageCompositionContent from 'models/composition/conversation_message_composition_content';

class CreateInstagramComposition extends CreateComposition {
  constructor(context) {
    super(context, ConversationMessageCompositionContent);
  }

  _createContent(attrs) {
    const channelDisplayTitle = 'INSTAGRAM MESSAGING';
    const lastInstagramItem = this._findLastInstagramItem(attrs.conversationId);
    const conversation = this.customerStores.conversations.find(attrs.conversationId);

    const potentialFroms = this._getPotentialFroms(conversation);
    let companyAddress = _.get(potentialFroms, 'default.id');
    let companyHandle = _.get(potentialFroms, 'default.handle');
    if (lastInstagramItem) {
      const matchedAddress = _.find(potentialFroms.allFromAddresses, [
        'id',
        _.get(lastInstagramItem, 'content.companyAddress'),
      ]);
      if (matchedAddress) {
        companyAddress = matchedAddress.id;
        companyHandle = matchedAddress.handle;
      }
    }

    let companyDisplayName = this._normalizeInstagramHandle(companyHandle);
    let externalCustomerInstagramAddress = _.find(this.customerStores.profile.get().externalCustomerAddresses, {
      type: 'INSTAGRAM_DIRECT',
    });

    let customerDisplayName = _.get(externalCustomerInstagramAddress, 'displayName');
    return new ConversationMessageCompositionContent({
      channel: MessageChannelType.INSTAGRAM_DIRECT,
      channelDisplayTitle,
      companyAddress,
      companyDisplayName,
      customerAddress: _.get(externalCustomerInstagramAddress, 'id'),
      customerDisplayName,
    });
  }

  _normalizeInstagramHandle(handle) {
    if (_.startsWith(handle, '@')) {
      return handle;
    }
    return `@${handle}`;
  }

  _canCreateComposition() {
    const hasExternalAgentActions = this.context.stores.appFeatures.get().isEnabled('externalAgentActions');
    const channelConfiguration = this.context.stores.channelConfiguration.get();
    return (
      channelConfiguration && channelConfiguration.companyInstagramAddresses().length > 0 && hasExternalAgentActions
    );
  }

  _getPotentialFroms(conversation) {
    let channelConfiguration = this.context.stores.channelConfiguration.get();
    let instagramEndpoints = channelConfiguration
      ? channelConfiguration.getEndpointsOfType(MessageChannelType.INSTAGRAM_DIRECT)
      : [];
    let allFromAddresses = _.map(instagramEndpoints, endpoint => {
      return {
        handle: endpoint.configuration.handle,
        id: endpoint.address,
      };
    });
    let routingGroupId = conversation.assignee.routingGroupId;

    return this._getFroms({
      allFromAddresses,
      conversation,
      endpoints: instagramEndpoints,
      endpointType: MessageChannelType.INSTAGRAM_DIRECT,
      routingGroupId,
    });
  }

  _getFroms({ allFromAddresses, conversation, endpoints, endpointType, routingGroupId }) {
    let firstRoutingGroupNumber = _.chain(this.context.stores.communicationQueues.findAll())
      .filter(commQueue => commQueue.routingGroupId === routingGroupId)
      .map(commQueue => _.find(endpoints, endpoint => endpoint.id === commQueue.endpointId))
      .compact()
      .map(endpoint => {
        return {
          handle: endpoint.configuration.handle,
          id: endpoint.address,
        };
      })
      .head()
      .value();

    const channelConfiguration = this.context.stores.channelConfiguration.get();
    const currentInbox = this.context.stores.routingGroups.findBy({ id: conversation.assignee.routingGroupId });
    const defaultEndpointId = currentInbox && currentInbox.defaultEndpoints[endpointType];
    const defaultEndpoint =
      channelConfiguration && _.find(channelConfiguration.endpoints, e => e.id === defaultEndpointId);
    const defaultAddress = defaultEndpoint
      ? {
          handle: defaultEndpoint.configuration.handle,
          id: defaultEndpoint.address,
        }
      : undefined;

    return {
      default: defaultAddress || firstRoutingGroupNumber || allFromAddresses[0],
      preferred: firstRoutingGroupNumber,
      allFromAddresses,
    };
  }

  _findLastInstagramItem(conversationId) {
    const customerId = this.context.stores.currentLocation.get().customerId;
    const { conversationHistory } = this.context.stores.customers.storesFor(customerId);
    return getLatestManualItem({
      conversationHistory,
      filter: item => item.content.channel === MessageChannelType.INSTAGRAM_DIRECT,
      conversationId,
    });
  }
}

export default CreateInstagramComposition;
